import { useState, useCallback, useRef } from 'react'
import { Modal, btnPrimary, btnSecondary, colors, input, label as labelStyle } from '../../components/ui'
import { api, ApiError } from '../../services/api'

interface PackageRow {
  readonly id: string
  readonly version: string
  readonly platform: string
}

interface SiteRow {
  readonly id: string
  readonly name: string
  readonly organizationId: string
  readonly organizationName: string | null
  readonly lastObservedPlatform: string | null
}

export interface UdmRolloutModalEditingRow {
  readonly id: string
  readonly version: string
  readonly status: 'canary' | 'general' | 'paused'
  readonly mandatory: boolean
  readonly notes: string
  readonly siteIds: readonly string[]
}

export interface UdmRolloutModalProps {
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly editingRow: UdmRolloutModalEditingRow | null
  readonly onSaved: () => void
}

interface WarningItem {
  readonly siteId: string
  readonly platform: string
}

export function UdmRolloutModal({ isOpen, onClose, editingRow, onSaved }: UdmRolloutModalProps) {
  const [packages, setPackages] = useState<readonly PackageRow[]>([])
  const [sites, setSites] = useState<readonly SiteRow[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [warnings, setWarnings] = useState<readonly WarningItem[]>([])

  // Form fields
  const [version, setVersion] = useState('')
  const [status, setStatus] = useState<'canary' | 'general' | 'paused'>('canary')
  const [mandatory, setMandatory] = useState(false)
  const [notes, setNotes] = useState('')
  const [selectedSiteIds, setSelectedSiteIds] = useState<readonly string[]>([])

  // Track which mode we've loaded to avoid re-fetching on every render
  const loadedForRef = useRef<string | null>(null)
  const modeKey = isOpen ? (editingRow ? `edit:${editingRow.id}` : 'create') : null

  const loadLookups = useCallback(async () => {
    setLoading(true)
    setSaveError(null)
    setWarnings([])
    try {
      const [pkgRes, siteRes] = await Promise.all([
        api.get<readonly PackageRow[]>('/fleet/packages'),
        api.get<readonly SiteRow[]>('/fleet/sites'),
      ])
      setPackages(pkgRes.data ?? [])
      setSites(siteRes.data ?? [])
    } catch {
      setSaveError('Failed to load lookup data')
    } finally {
      setLoading(false)
    }
  }, [])

  // Trigger load + prefill whenever the modal opens or switches between create/edit
  if (modeKey !== null && loadedForRef.current !== modeKey) {
    loadedForRef.current = modeKey
    void loadLookups()
    if (editingRow) {
      setVersion(editingRow.version)
      setStatus(editingRow.status)
      setMandatory(editingRow.mandatory)
      setNotes(editingRow.notes)
      setSelectedSiteIds(editingRow.siteIds)
    } else {
      setVersion('')
      setStatus('canary')
      setMandatory(false)
      setNotes('')
      setSelectedSiteIds([])
    }
    setWarnings([])
  }
  if (!isOpen && loadedForRef.current !== null) {
    loadedForRef.current = null
  }

  // Deduplicate versions from packages list
  const uniqueVersions = [...new Set(packages.map((p) => p.version))].sort((a, b) => b.localeCompare(a))

  // Group sites by org for the picker
  const orgGroups = sites.reduce<Record<string, { name: string; sites: SiteRow[] }>>((acc, s) => {
    const orgId = s.organizationId
    const orgName = s.organizationName ?? orgId
    if (!acc[orgId]) acc[orgId] = { name: orgName, sites: [] }
    acc[orgId]!.sites.push(s)
    return acc
  }, {})

  const toggleSite = (siteId: string) => {
    setSelectedSiteIds((prev) => (prev.includes(siteId) ? prev.filter((id) => id !== siteId) : [...prev, siteId]))
  }

  const handleSave = useCallback(async () => {
    setSaveError(null)
    setWarnings([])
    setSaving(true)

    const body = {
      version: version.trim(),
      status,
      mandatory,
      notes: notes.trim() || undefined,
      siteIds: status === 'canary' ? [...selectedSiteIds] : [],
    }

    try {
      if (editingRow) {
        await api.patch(`/admin/udm/rollouts/${editingRow.id}`, {
          status: body.status,
          mandatory: body.mandatory,
          notes: body.notes,
          siteIds: body.siteIds,
        })
        onSaved()
      } else {
        const res = await api.post<{ id: string }>('/admin/udm/rollouts', body)
        const responseWarnings = (res as unknown as { warnings?: readonly WarningItem[] }).warnings ?? []
        if (responseWarnings.length > 0) {
          setWarnings(responseWarnings)
          // Do not close — show warnings inline so user can review
        } else {
          onSaved()
        }
      }
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }, [version, status, mandatory, notes, selectedSiteIds, editingRow, onSaved])

  const sectionHeader: React.CSSProperties = {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: colors.textHeading,
    marginBottom: '0.5rem',
    marginTop: '1.25rem',
  }

  const footer: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    marginTop: '1.5rem',
    paddingTop: '1rem',
    borderTop: `1px solid ${colors.border}`,
  }

  const title = editingRow ? `Edit rollout: ${editingRow.version}` : 'New UDM Rollout'

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} width="600px">
      {loading ? (
        <div style={{ color: colors.textMuted, padding: '1rem 0' }}>Loading...</div>
      ) : (
        <>
          {/* Version select (create only) */}
          {!editingRow && (
            <div>
              <label htmlFor="rollout-version" style={labelStyle}>
                Version *
              </label>
              <select
                id="rollout-version"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                style={{ ...input, width: '100%' }}
              >
                <option value="">Select a version...</option>
                {uniqueVersions.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
              {uniqueVersions.length === 0 && (
                <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '0.25rem' }}>
                  No packages uploaded yet. Upload a package first.
                </div>
              )}
            </div>
          )}

          {/* Status select */}
          <div style={{ marginTop: editingRow ? 0 : '0.75rem' }}>
            <label htmlFor="rollout-status" style={labelStyle}>
              Status *
            </label>
            <select
              id="rollout-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as 'canary' | 'general' | 'paused')}
              style={{ ...input, width: '100%' }}
            >
              <option value="canary">Canary (targeted sites only)</option>
              <option value="general">General (all sites)</option>
              <option value="paused">Paused</option>
            </select>
          </div>

          {/* Mandatory checkbox */}
          <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              id="rollout-mandatory"
              type="checkbox"
              checked={mandatory}
              onChange={(e) => setMandatory(e.target.checked)}
            />
            <label htmlFor="rollout-mandatory" style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer' }}>
              Mandatory (UDM will force-update even if currently running)
            </label>
          </div>

          {/* Notes */}
          <div style={{ marginTop: '0.75rem' }}>
            <label htmlFor="rollout-notes" style={labelStyle}>
              Notes (optional)
            </label>
            <input
              id="rollout-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Release notes or reason for this rollout"
              style={{ ...input, width: '100%' }}
            />
          </div>

          {/* Site picker — only relevant for canary */}
          {status === 'canary' && (
            <>
              <div style={sectionHeader}>
                Targeted Sites{' '}
                <span style={{ fontSize: '0.75rem', color: colors.textMuted, fontWeight: 400 }}>
                  ({selectedSiteIds.length} selected)
                </span>
              </div>
              <div
                style={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: '4px',
                  maxHeight: '220px',
                  overflowY: 'auto',
                  padding: '0.5rem',
                }}
              >
                {Object.entries(orgGroups).map(([orgId, group]) => (
                  <div key={orgId} style={{ marginBottom: '0.75rem' }}>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        color: colors.textMuted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '0.25rem',
                      }}
                    >
                      {group.name}
                    </div>
                    {group.sites.map((s) => (
                      <label
                        key={s.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.2rem 0.25rem',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedSiteIds.includes(s.id)}
                          onChange={() => toggleSite(s.id)}
                        />
                        <span>{s.name}</span>
                        {s.lastObservedPlatform && (
                          <span
                            style={{
                              fontSize: '0.75rem',
                              color: colors.textMuted,
                              fontFamily: 'monospace',
                            }}
                          >
                            {s.lastObservedPlatform}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                ))}
                {sites.length === 0 && (
                  <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>No sites available.</div>
                )}
              </div>
            </>
          )}

          {/* Warnings from create response */}
          {warnings.length > 0 && (
            <div
              style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                background: '#fef3c7',
                border: '1px solid #fde68a',
                borderRadius: '4px',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#92400e', marginBottom: '0.5rem' }}>
                Rollout created with warnings — the following targeted sites have no matching package for this version +
                platform:
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.8125rem', color: '#92400e' }}>
                {warnings.map((w) => {
                  const site = sites.find((s) => s.id === w.siteId)
                  return (
                    <li key={w.siteId}>
                      {site ? site.name : w.siteId} — {w.platform}
                    </li>
                  )
                })}
              </ul>
              <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: '#92400e' }}>
                Upload packages for these sites before the rollout takes effect.
              </div>
            </div>
          )}

          {saveError && (
            <div style={{ color: colors.danger, fontSize: '0.875rem', marginTop: '0.75rem' }}>{saveError}</div>
          )}

          <div style={footer}>
            <button type="button" style={btnSecondary} onClick={onClose} disabled={saving}>
              {warnings.length > 0 ? 'Close' : 'Cancel'}
            </button>
            {warnings.length === 0 && (
              <button type="button" style={btnPrimary} onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving...' : editingRow ? 'Save Changes' : 'Create Rollout'}
              </button>
            )}
          </div>
        </>
      )}
    </Modal>
  )
}
